import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Storyboard } from './types';
import generateScriptPdf, { ScriptPdfReference } from './generateScriptPdf';

interface Props {
  open: boolean;
  onClose: () => void;
  // True while the parent is still loading the script detail + references.
  loading: boolean;
  storyboard: Storyboard | null;
  productName: string | null;
  references: ScriptPdfReference[];
  // The script text to prefill (the generated read-through). Editable here
  // because the final script is often human-edited after generation.
  initialScript: string;
}

const DEFAULT_INCLUDE = { script: true, videoLink: true, keyframes: true, transcript: true, shotByShot: true };
type IncludeKey = keyof typeof DEFAULT_INCLUDE;

// Pre-download dialog for the creator-brief PDF: edit the script and pick which
// sections to include, then download.
export default function ScriptBriefDialog({ open, onClose, loading, storyboard, productName, references, initialScript }: Props) {
  const [scriptText, setScriptText] = useState('');
  const [include, setInclude] = useState(DEFAULT_INCLUDE);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the editable fields whenever the dialog (re-)opens or the loaded
  // script changes.
  useEffect(() => {
    if (open) {
      setScriptText(initialScript);
      setInclude(DEFAULT_INCLUDE);
      setError(null);
    }
  }, [open, initialScript]);

  const toggle = (key: IncludeKey) => setInclude(p => ({ ...p, [key]: !p[key] }));

  const download = async () => {
    if (!storyboard) return;
    setGenerating(true);
    setError(null);
    try {
      await generateScriptPdf(storyboard, productName, references, { scriptText, include });
      onClose();
    } catch {
      setError('Failed to generate the PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const hasRefs = references.length > 0;
  return (
    <Dialog open={open} onClose={() => !generating && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Download creator brief</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : !storyboard ? (
          <Alert severity="error">Couldn&apos;t load this script.</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Edit the script (it may have changed since generation) and choose which sections to include.
            </Typography>
            <FormGroup row sx={{ mb: 1 }}>
              <FormControlLabel control={<Checkbox size="small" checked={include.script} onChange={() => toggle('script')} />} label="Script" />
              <FormControlLabel control={<Checkbox size="small" checked={include.videoLink} onChange={() => toggle('videoLink')} disabled={!hasRefs} />} label="Video link" />
              <FormControlLabel control={<Checkbox size="small" checked={include.keyframes} onChange={() => toggle('keyframes')} disabled={!hasRefs} />} label="Keyframes" />
              <FormControlLabel control={<Checkbox size="small" checked={include.transcript} onChange={() => toggle('transcript')} disabled={!hasRefs} />} label="Original transcript" />
              <FormControlLabel control={<Checkbox size="small" checked={include.shotByShot} onChange={() => toggle('shotByShot')} />} label="Shot-by-shot" />
            </FormGroup>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>Script</Typography>
            <TextField
              value={scriptText}
              onChange={e => setScriptText(e.target.value)}
              fullWidth multiline minRows={10} maxRows={24}
              variant="outlined"
              disabled={generating || !include.script}
              placeholder="The script the creator will perform…"
            />
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={generating}>Cancel</Button>
        <Button variant="contained" disabled={generating || loading || !storyboard} onClick={download}
          startIcon={generating ? <CircularProgress size={14} color="inherit" /> : undefined}>
          {generating ? 'Preparing…' : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
